import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

export interface DecisionTreeFormData {
  id?: string;
  treeKey: string;
  name: string;
  description: string | null;
  icon: string;
  displayOrder: number;
  isActive: boolean;
}

interface CreateEditDecisionTreeModalProps {
  open: boolean;
  isEdit: boolean;
  onClose: () => void;
  onSave: (data: DecisionTreeFormData) => void;
  saving: boolean;
  saveError: string | null;
}

const CreateEditDecisionTreeModal: React.FC<CreateEditDecisionTreeModalProps> = ({
  open,
  isEdit,
  onClose,
  onSave,
  saving,
  saveError,
}) => {
  const { selectedTree, loading } = useAppSelector((state) => state.decisionTrees);

  const [formData, setFormData] = useState({
    treeKey: '',
    name: '',
    description: '',
    icon: '',
    displayOrder: 1,
    isActive: true,
  });

  useEffect(() => {
    if (isEdit && selectedTree) {
      setFormData({
        treeKey: selectedTree.treeKey || '',
        name: selectedTree.name || '',
        description: selectedTree.description || '',
        icon: selectedTree.icon || '',
        displayOrder: selectedTree.displayOrder ?? 1,
        isActive: selectedTree.isActive ?? true,
      });
    } else if (!isEdit) {
      setFormData({
        treeKey: '',
        name: '',
        description: '',
        icon: '',
        displayOrder: 1,
        isActive: true,
      });
    }
  }, [selectedTree, isEdit, open]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const data: DecisionTreeFormData = {
      treeKey: formData.treeKey,
      name: formData.name,
      description: formData.description.trim() || null,
      icon: formData.icon,
      displayOrder: formData.displayOrder,
      isActive: formData.isActive,
    };

    if (isEdit && selectedTree) {
      data.id = selectedTree.id;
    }

    onSave(data);
  };

  const isFormValid = formData.treeKey && formData.name;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Árbol de Decisiones' : 'Crear Árbol de Decisiones'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}

            <TextField
              label="Clave (treeKey)"
              value={formData.treeKey}
              onChange={(e) => handleChange('treeKey', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={saving}
            />

            <TextField
              label="Icono"
              value={formData.icon}
              onChange={(e) => handleChange('icon', e.target.value)}
              fullWidth
              disabled={saving}
              helperText="Nombre del icono de Material UI (ej: Source, Home, Settings)"
            />

            <TextField
              label="Orden de Visualización"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => handleChange('displayOrder', parseInt(e.target.value, 10) || 1)}
              fullWidth
              disabled={saving}
              inputProps={{ min: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Activo"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !isFormValid}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditDecisionTreeModal;
